/**
 * One navigation model, rendered twice: into the sidebar on a desktop and
 * into the bottom bar on a phone. Two lists would drift.
 */
export interface NavItem {
  to: string
  label: string
  /** An id from `brand/icons/sprite.svg`, without the `rf-` prefix. */
  icon: string
  /** Match this route exactly. Needed for `/`, which prefixes everything. */
  exact?: boolean
}

export interface NavSection {
  header?: string
  items: NavItem[]
}

/**
 * The five that reach the bottom bar. A phone is used one-handed in a kitchen
 * or a supermarket aisle (N-04), so this is the short list of what someone
 * standing up actually needs; the aisle is why Shopping is here and the
 * household settings are behind the menu button in the header instead.
 */
export const PRIMARY_NAV: NavItem[] = [
  { to: '/', label: 'Today', icon: 'eat', exact: true },
  { to: '/week', label: 'Week', icon: 'plan' },
  { to: '/inventory', label: 'Kitchen', icon: 'fridge' },
  { to: '/recipes', label: 'Recipes', icon: 'recipe' },
  { to: '/shopping', label: 'Shopping', icon: 'shopping' },
]

/**
 * The sidebar, which has room for everything. Sections are plain headers
 * rather than collapsing groups: collapsing would hide the thing the sidebar
 * exists to put in front of you.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Today', icon: 'eat', exact: true },
      { to: '/everyone', label: 'Everyone today', icon: 'weight' },
      { to: '/week', label: 'The week', icon: 'plan' },
      { to: '/plan', label: 'Plan my week', icon: 'target' },
    ],
  },
  {
    header: 'Kitchen',
    items: [
      { to: '/inventory', label: 'In the house', icon: 'fridge' },
      { to: '/use-soon', label: 'Use it up', icon: 'timer' },
      { to: '/scan', label: 'Scan something in', icon: 'scan' },
      { to: '/capture', label: 'Add a product', icon: 'add' },
      { to: '/shopping', label: 'Shopping', icon: 'shopping' },
    ],
  },
  {
    header: 'Recipes',
    items: [
      { to: '/recipes', label: 'All recipes', icon: 'recipe' },
      { to: '/books', label: 'Books', icon: 'recipe' },
      { to: '/recipes/import', label: 'Import from a link', icon: 'add' },
      { to: '/imports', label: 'Import from a file', icon: 'add' },
      { to: '/foods', label: 'Foods', icon: 'cupboard' },
    ],
  },
  {
    header: 'Training',
    items: [
      { to: '/training', label: 'Sessions', icon: 'train' },
      { to: '/review', label: 'How the week went', icon: 'repeat' },
    ],
  },
  {
    header: 'Household',
    items: [
      { to: '/settings', label: 'My settings', icon: 'target' },
      { to: '/people', label: 'People and targets', icon: 'weight' },
      { to: '/household', label: 'Members and settings', icon: 'target' },
      // Scales, health services, one day a supermarket: a household matter, not a training one.
      { to: '/connections', label: 'Connections', icon: 'repeat' },
    ],
  },
]
